<template>
  <aside
    class="message-detail border rounded bg-body-emphasis"
    aria-label="Message detail"
  >
    <header class="d-flex align-items-start gap-2 border-bottom p-3">
      <div class="min-w-0 flex-grow-1">
        <h5 class="mb-1 text-truncate">
          {{
            message.conversation_name ??
            message.conversation_id ??
            "No conversation"
          }}
        </h5>
        <div class="small text-muted">
          {{ message.sender ?? "Unknown sender" }} ·
          <time :datetime="message.timestamp"
            >{{ formatDayLabel(utcDay(message.timestamp)) }}
            {{ formatClock(message.timestamp) }} UTC</time
          >
        </div>
      </div>
      <button
        class="btn btn-sm btn-outline-secondary"
        title="Copy link to this message"
        @click="copyLink"
      >
        <FontAwesomeIcon icon="copy" /><span class="visually-hidden"
          >Copy link</span
        >
      </button>
      <button
        class="btn btn-sm btn-outline-secondary"
        title="Close (Esc)"
        @click="$emit('close')"
      >
        <FontAwesomeIcon icon="xmark" /><span class="visually-hidden"
          >Close</span
        >
      </button>
    </header>
    <div class="p-3 d-grid gap-3">
      <TradeChatHighlightComponent
        class="body p-2 rounded"
        :text="message.body ?? ''"
        :terms
      />

      <section class="p-0" aria-label="Trade signals">
        <h6 class="section-title">Trade signals</h6>
        <p v-if="!message.trade_signals.length" class="small text-muted mb-0">
          No trade signal was extracted from this message.
        </p>
        <div v-else class="table-responsive">
          <table class="table table-sm small mb-0">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col" class="text-end">Price</th>
                <th scope="col" class="text-end">Volume</th>
                <th scope="col">Product</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="signal in message.trade_signals" :key="signal.id">
                <td>
                  <span
                    class="badge"
                    :class="signalBadge(signal.signal_type)"
                    >{{ signal.signal_type }}</span
                  >
                </td>
                <td class="text-end text-nowrap">
                  {{ signal.price
                  }}{{
                    signal.ask_price !== null ? ` / ${signal.ask_price}` : ""
                  }}
                  {{ signal.currency }}
                </td>
                <td class="text-end text-nowrap">
                  {{
                    Number(signal.volume_mwh)
                      ? `${formatMwh(Number(signal.volume_mwh))} MWh`
                      : "—"
                  }}
                </td>
                <td>
                  {{
                    [
                      signal.product,
                      ...signal.technology,
                      ...signal.origin,
                      ...signal.production_years,
                    ].join(" · ")
                  }}
                  <div
                    v-if="signal.counterparty || signal.notes"
                    class="text-muted"
                  >
                    {{
                      [signal.counterparty, signal.notes]
                        .filter(Boolean)
                        .join(" — ")
                    }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section
        v-if="message.conversation_id"
        class="p-0"
        aria-label="Conversation context"
      >
        <div class="d-flex justify-content-between align-items-center">
          <h6 class="section-title mb-0">In context</h6>
          <button
            class="btn btn-link btn-sm p-0"
            @click="$emit('openConversation', message.conversation_id)"
          >
            Open conversation
          </button>
        </div>
        <ol class="context list-unstyled mb-0 mt-2">
          <li v-for="entry in transcript" :key="entry.id">
            <button
              class="context-row"
              :class="{ current: entry.id === message.id }"
              :aria-current="entry.id === message.id ? 'true' : undefined"
              :disabled="entry.id === message.id"
              @click="$emit('select', entry.id)"
            >
              <time class="text-muted" :datetime="entry.timestamp">{{
                formatClock(entry.timestamp)
              }}</time>
              <span class="min-w-0"
                ><strong>{{ entry.sender ?? "Unknown" }}</strong>
                <span
                  v-if="entry.trade_signal_count"
                  class="badge bg-success-subtle text-success-emphasis ms-1"
                  >{{ entry.trade_signal_count }}</span
                >
                <TradeChatHighlightComponent
                  class="d-block"
                  :text="entry.body ?? ''"
                  :terms
              /></span>
            </button>
          </li>
        </ol>
      </section>

      <details class="small">
        <summary>Source record</summary>
        <dl class="meta mt-2 mb-2">
          <dt>Message id</dt>
          <dd>{{ message.id }}</dd>
          <dt>Source</dt>
          <dd>{{ message.source }} · {{ message.channel }}</dd>
          <dt>External id</dt>
          <dd class="text-break">{{ message.external_id }}</dd>
          <dt>Conversation id</dt>
          <dd class="text-break">{{ message.conversation_id ?? "—" }}</dd>
          <dt>Sent at</dt>
          <dd>{{ message.sent_at ?? "not reported by source" }}</dd>
          <dt>Captured at</dt>
          <dd>{{ message.captured_at ?? "—" }}</dd>
          <dt>Stored at</dt>
          <dd>{{ message.created_at }}</dd>
        </dl>
        <pre v-if="message.payload" class="payload p-2 rounded mb-0">{{
          JSON.stringify(message.payload, null, 2)
        }}</pre>
      </details>
    </div>
  </aside>
</template>
<script setup lang="ts">
import { computed } from "vue";
import { formatMwh } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import TradeChatHighlightComponent from "./TradeChatHighlightComponent.vue";
import { formatClock, formatDayLabel, utcDay } from "./tradeChatLogsPresenters";
import type { ChatMessageDetail } from "./tradeChatLogsTypes";

const props = defineProps<{ message: ChatMessageDetail; terms: string[] }>();
defineEmits<{
  close: [];
  select: [id: number];
  openConversation: [conversationId: string];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const SIGNAL_BADGES: Record<string, string> = {
  Trade: "bg-success",
  Bid: "bg-primary",
  Ask: "bg-warning text-dark",
  Market: "bg-info text-dark",
  Off: "bg-secondary",
};

const transcript = computed(() => [
  ...props.message.context_before,
  props.message,
  ...props.message.context_after,
]);

const signalBadge = (type: string) => SIGNAL_BADGES[type] ?? "bg-secondary";

async function copyLink() {
  try {
    await navigator.clipboard.writeText(
      new URL(props.message.admin_url, window.location.origin).toString()
    );
    globalSuccessHandler(
      "Link copied",
      "Anyone with admin access can open this message."
    );
  } catch (error) {
    globalErrorHandler(error);
  }
}
</script>
<style scoped lang="scss">
.message-detail {
  @media (min-width: 901px) {
    position: sticky;
    top: 1rem;
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
}
.min-w-0 {
  min-width: 0;
}
.section-title {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--phoenix-secondary-color);
}
.body {
  display: block;
  background: var(--phoenix-body-bg);
  font-family: var(--phoenix-font-monospace);
  font-size: 0.9rem;
}
.context-row {
  display: grid;
  grid-template-columns: 4.5rem minmax(0, 1fr);
  gap: 0.5rem;
  width: 100%;
  text-align: left;
  padding: 0.35rem 0.5rem;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  font-size: 0.8rem;

  &:hover:not(:disabled) {
    background: var(--phoenix-secondary-bg);
  }
  &.current {
    background: var(--phoenix-primary-bg-subtle);
    opacity: 1;
  }
}
.meta {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 0.15rem 0.75rem;

  dd {
    margin: 0;
  }
}
.payload {
  max-height: 320px;
  overflow: auto;
  background: var(--phoenix-body-bg);
  font-size: 0.75rem;
}
</style>
